import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Restore as RestoreIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

interface AutoBackupDialogProps {
  open: boolean;
  onClose: () => void;
}

interface BackupRecord {
  id: string;
  date: Date;
  status: 'COMPLETED' | 'FAILED' | 'RUNNING';
  type: 'manual' | 'scheduled';
  createdBy?: string;
  size?: string;
}

const AutoBackupDialog: React.FC<AutoBackupDialogProps> = ({ open, onClose }) => {
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<BackupRecord | null>(null);
  const [confirmationWord, setConfirmationWord] = useState('');

  // Mock data - Last 5 days of backups (today: January 29, 2026 Thursday)
  const mockBackups: BackupRecord[] = [
    {
      id: '2026-01-29T02-00-00-000Z',
      date: new Date(2026, 0, 29, 2, 0, 0),
      status: 'COMPLETED',
      type: 'scheduled',
      size: '124.5 MB',
    },
    {
      id: '2026-01-28T02-00-00-000Z',
      date: new Date(2026, 0, 28, 2, 0, 0),
      status: 'COMPLETED',
      type: 'scheduled',
      size: '118.3 MB',
    },
    {
      id: '2026-01-27T02-00-00-000Z',
      date: new Date(2026, 0, 27, 2, 0, 0),
      status: 'COMPLETED',
      type: 'scheduled',
      size: '112.7 MB',
    },
    {
      id: '2026-01-26T02-00-00-000Z',
      date: new Date(2026, 0, 26, 2, 0, 0),
      status: 'COMPLETED',
      type: 'scheduled',
      size: '108.9 MB',
    },
    {
      id: '2026-01-25T02-00-00-000Z',
      date: new Date(2026, 0, 25, 2, 0, 0),
      status: 'COMPLETED',
      type: 'scheduled',
      size: '105.2 MB',
    },
  ];

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'FAILED':
        return 'error';
      case 'RUNNING':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type: string) => {
    return type === 'manual' ? 'Manual' : 'Scheduled';
  };

  const handleCopyCommand = (backup: BackupRecord) => {
    const command = `gsutil -m cp -r gs://alexapay-backups-eu/backups/firestore/${backup.id} ./`;
    navigator.clipboard.writeText(command);
    toast.success('Download command copied to clipboard!');
  };

  const handleOpenRestore = (backup: BackupRecord) => {
    setSelectedBackup(backup);
    setConfirmationWord('');
    setRestoreDialogOpen(true);
  };

  const handleRestore = () => {
    if (confirmationWord !== 'bengisu') {
      toast.error('Invalid confirmation word!');
      return;
    }

    // Mock restore operation
    toast.info('Restore operation started... (Demo mode)');
    setRestoreDialogOpen(false);
    setConfirmationWord('');
  };

  const handleCloseRestoreDialog = () => {
    setRestoreDialogOpen(false);
    setConfirmationWord('');
    setSelectedBackup(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: 'white',
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon />
            <Typography variant="h6" component="span">
              Automated Backups
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            backgroundColor: 'white',
            p: 3,
          }}
        >
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Last 5 automated backups. Automatic backups are performed daily at 02:00 AM (Europe/Istanbul timezone). Backups older than 5 days are automatically deleted.
            </Typography>
          </Alert>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell><strong>Date & Time</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Size</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mockBackups.map((backup) => (
                  <TableRow
                    key={backup.id}
                    hover
                    sx={{
                      '&:hover': { backgroundColor: '#f9f9f9' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(backup.date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={backup.status === 'COMPLETED' ? 'Completed' : backup.status}
                        color={getStatusColor(backup.status)}
                        size="small"
                        icon={backup.status === 'COMPLETED' ? <SuccessIcon /> : <ErrorIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getTypeLabel(backup.type)}
                        size="small"
                        variant="outlined"
                        icon={backup.type === 'manual' ? <PersonIcon /> : <ScheduleIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {backup.size}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Tooltip title="Copy download command">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyCommand(backup)}
                            sx={{
                              color: '#667eea',
                              '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.1)' },
                            }}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Restore">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenRestore(backup)}
                            disabled={backup.status !== 'COMPLETED'}
                            sx={{
                              color: '#764ba2',
                              '&:hover': { backgroundColor: 'rgba(118, 75, 162, 0.1)' },
                            }}
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0, backgroundColor: 'white' }}>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog
        open={restoreDialogOpen}
        onClose={handleCloseRestoreDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#f44336', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RestoreIcon />
            <Typography variant="h6" component="span">
              Restore Confirmation
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold">
              ⚠️ Warning: This action cannot be undone!
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              The current database will be replaced with the selected backup.
            </Typography>
          </Alert>

          {selectedBackup && (
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Backup Date:</strong> {formatDate(selectedBackup.date)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Size:</strong> {selectedBackup.size}
              </Typography>
            </Box>
          )}

          <Typography variant="body2" sx={{ mb: 2 }}>
            To proceed, please type the confirmation word below:
          </Typography>

          <Box
            sx={{
              p: 2,
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: 1,
              mb: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" fontWeight="bold" color="#856404">
              bengisu
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Confirmation Word"
            value={confirmationWord}
            onChange={(e) => setConfirmationWord(e.target.value)}
            autoFocus
            helperText={
              confirmationWord && confirmationWord !== 'bengisu'
                ? 'Wrong word!'
                : 'Type the word above exactly'
            }
            error={confirmationWord !== '' && confirmationWord !== 'bengisu'}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseRestoreDialog} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleRestore}
            variant="contained"
            color="error"
            disabled={confirmationWord !== 'bengisu'}
            startIcon={<RestoreIcon />}
          >
            Restore
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AutoBackupDialog;
